import { useEffect } from "react";

const TEXT_ZOOM_CSS_VAR = "--text-zoom";
const DEFAULT_REM_BASE_PX = 16;

/**
 * Detects the Android WebView text zoom level and sets a CSS variable `--text-zoom`
 * on the document root element.
 *
 * In Android WebView, `textZoom` only scales `font-size` CSS properties.
 * Width/height dimensions (even in `em`/`rem`) remain unscaled.
 * This hook bridges that gap by exposing the zoom factor as a CSS variable,
 * which can then be used via `calc()` in any dimension that needs to scale.
 *
 * Usage in SCSS:
 *   @include scalable-icon-size(1rem);
 *   // → width: calc(1rem * var(--text-zoom, 1))
 *
 * Call this hook once at the app root level (e.g. MainHeader).
 */
export const useTextZoom = (): void => {
  useEffect(() => {
    // Measure the actual rendered size of 1rem (post-textZoom)
    // using a temporary element to avoid depending on a hardcoded base value.
    const testEl = document.createElement("div");
    testEl.style.cssText =
      "position:absolute;visibility:hidden;font-size:1rem;";
    document.body.appendChild(testEl);
    const renderedRem = parseFloat(
      window.getComputedStyle(testEl).fontSize
    );
    document.body.removeChild(testEl);

    const zoomFactor = renderedRem / DEFAULT_REM_BASE_PX;
    const safeZoomFactor =
      Number.isFinite(zoomFactor) && zoomFactor > 0 ? zoomFactor : 1;

    document.documentElement.style.setProperty(
      TEXT_ZOOM_CSS_VAR,
      safeZoomFactor.toString()
    );
  }, []);
};
